// src/hr/portal/RequestsPage.js -- HR's request queue (mockup R3): Waiting on HR / Waiting on manager /
// Decided, a progress chip per step, and the actions HR may take on each row:
//   pending_hr       Sign off / Decline (optional reason), when this HR person may sign it off
//   pending_manager  Approve for manager -- always sent with onBehalf: true, also when the step has
//                    fallen to HR because the approver left
//   needsBackup      an HR person's own request nobody can sign off: link to Settings
// A refused action (403 / 409) shows the server's message on that row. Rows carry no notes.
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheck, FiCheckCircle, FiInbox, FiSettings, FiX } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, EmptyState, Field, Pill, Skeleton } from '../kit';
import { plainId } from '../kit/util';
import { daysLabel, shortRangeLabel } from '../format';
import { typeTone } from '../timeoff/labels';
import { fullName, shortName } from './portalUtils';
import { shortDate } from '../timeoff/dates';

const VIEWS = [
  { key: 'hr', label: 'Waiting on HR', count: 'hr' },
  { key: 'manager', label: 'Waiting on manager', count: 'manager' },
  { key: 'decided', label: 'Decided' }
];
const CAPTIONS = { hr: 'Requests waiting on HR', manager: 'Requests waiting on a manager', decided: 'Requests decided in the last 90 days' };

const lengthOf = (item) => (Number(item.totalHours) === 4 ? 'Half day' : daysLabel(item.totalHours));

// The queue for one view; reload() keeps the rows on screen until the new answer arrives.
function useQueue(view, onCounts) {
  const [state, setState] = useState({ view, status: 'loading', data: null });
  const latest = useRef(0);
  const countsRef = useRef(onCounts);
  countsRef.current = onCounts;

  const load = useCallback(async (which, keep) => {
    latest.current += 1;
    const call = latest.current;
    setState((s) => (keep && s.view === which ? { ...s, status: 'loading' } : { view: which, status: 'loading', data: null }));
    try {
      const body = await hr.admin.requests.list({ view: which });
      if (call !== latest.current) return;
      setState({ view: which, status: 'ready', data: body || { requests: [], counts: null } });
      if (body && body.counts && countsRef.current) countsRef.current(body.counts);
    } catch (err) {
      if (call === latest.current) setState((s) => ({ ...s, view: which, status: 'error' }));
    }
  }, []);

  useEffect(() => {
    load(view, false);
    return () => { latest.current += 1; };
  }, [view, load]);

  const reload = useCallback(() => load(view, true), [load, view]);
  return { ...state, reload };
}

function StageChips({ item }) {
  const manager = shortName(item.managerName) || 'Approver';
  if (item.status === 'approved') return <Pill tone="ok" icon={<FiCheck />}>Approved</Pill>;
  if (item.status === 'declined') return <Pill tone="bad" icon={<FiX />}>Declined</Pill>;
  if (item.status === 'cancelled') return <Pill tone="mute">Cancelled</Pill>;

  let steps;
  let note = null;
  if (item.status === 'pending_hr') {
    steps = item.managerDecidedAt
      ? [{ state: 'done', mark: <FiCheck />, label: manager, sr: `${item.managerName || 'Approver'} approved` }, { state: 'now', mark: '2', label: 'HR', sr: 'waiting on HR' }]
      : [{ state: 'now', mark: '1', label: 'HR', sr: 'waiting on HR (no approver step)' }];
  } else {
    const gone = item.stage === 'hr';
    steps = [
      { state: gone ? 'gone' : 'now', mark: '1', label: manager, sr: gone ? `${item.managerName || 'approver'} is no longer here` : `waiting on ${item.managerName || 'the approver'}` },
      { state: 'next', mark: '2', label: 'HR', sr: 'then HR' }
    ];
    if (gone) note = 'Their approver has left — HR can approve for them.';
  }
  return (
    <div className="hrp-stage-wrap">
      <ol className="hrp-stage" aria-label="Progress">
        {steps.map((s, i) => (
          <li key={s.label + i} className="hrp-stage__st" data-state={s.state}>
            <i aria-hidden="true">{s.mark}</i>
            <span aria-hidden="true">{s.label}</span>
            <span className="hrp-sr">{s.sr}</span>
          </li>
        ))}
      </ol>
      {note && <p className="hrp-stage__note">{note}</p>}
    </div>
  );
}

function waitingText(item) {
  if (item.waitingDays == null) return null;
  if (item.waitingDays <= 0) return { text: 'Waiting since today' };
  const text = `Waiting ${item.waitingDays} ${item.waitingDays === 1 ? 'workday' : 'workdays'}`;
  if (item.status === 'pending_manager' && item.waitingDays >= 4) return { text, tone: 'bad' };
  if (item.waitingDays >= 2) return { text, tone: 'warn' };
  return { text };
}

function RequestRows({ item, toneFor, onDecided }) {
  const [declining, setDeclining] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const reasonRef = useRef(null);
  const keepRef = useRef(null);
  const nameId = `hrp-rq-${plainId(useId())}`;
  const who = item.requester || {};
  const wait = waitingText(item);
  const open = item.status === 'pending_hr' || item.status === 'pending_manager';
  const forManager = item.status === 'pending_manager';
  // HR may cancel any waiting request and any approved one (the server has the final say, e.g. an HR
  // person's own request, and its refusal is shown on the row).
  const cancellable = open || item.status === 'approved';

  useEffect(() => {
    if (declining && reasonRef.current) reasonRef.current.focus();
  }, [declining]);
  useEffect(() => {
    if (cancelling && keepRef.current) keepRef.current.focus();
  }, [cancelling]);

  const startDecline = () => { setCancelling(false); setError(''); setDeclining(true); };
  const startCancel = () => { setDeclining(false); setError(''); setCancelling(true); };

  const decide = async (kind) => {
    setBusy(kind);
    setError('');
    let body;
    if (kind === 'onBehalf') body = { decision: 'approve', onBehalf: true };
    else if (kind === 'decline') {
      body = reason.trim() ? { decision: 'decline', reason: reason.trim() } : { decision: 'decline' };
      // A manager-step request is declined for the manager.
      if (forManager) body.onBehalf = true;
    } else body = { decision: 'approve' };
    try {
      const result = await hr.admin.requests.decide(item.id, body);
      setSaved(kind === 'decline' && forManager ? 'declineOnBehalf' : kind);
      setDeclining(false);
      onDecided({ item, kind, status: result && result.request ? result.request.status : null });
    } catch (err) {
      setError(errorText(err, "Couldn't save that decision. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    setBusy('cancel');
    setError('');
    try {
      await hr.admin.requests.cancel(item.id);
      setSaved('cancel');
      setCancelling(false);
      onDecided({ item, kind: 'cancel', status: 'cancelled' });
    } catch (err) {
      setError(errorText(err, "Couldn't cancel that request. Try again."));
    } finally {
      setBusy(null);
    }
  };

  const cancelLink = cancellable && !cancelling && (
    <button key="cx" type="button" className="hrp-link hrp-rq__ovr hrp-rq__cx" disabled={!!busy} aria-label={`Cancel ${who.name}'s request`} onClick={startCancel}>
      <FiX aria-hidden="true" />
      Cancel request
    </button>
  );

  let actions = null;
  if (saved) {
    const label = { approve: 'Signed off', decline: 'Declined', declineOnBehalf: 'Declined for manager', onBehalf: 'Approved for manager', cancel: 'Cancelled' }[saved];
    const bad = saved === 'decline' || saved === 'declineOnBehalf';
    if (saved === 'cancel') actions = <Pill tone="mute">{label}</Pill>;
    else actions = <Pill tone={bad ? 'bad' : 'ok'} icon={bad ? <FiX /> : <FiCheck />}>{label}</Pill>;
  } else if (open) {
    const parts = [];
    if (item.status === 'pending_hr' && item.canSignOff && !declining) {
      parts.push(<Btn key="no" variant="danger" size="sm" onClick={startDecline} disabled={!!busy} aria-label={`Decline ${who.name}'s request`}>Decline</Btn>);
      parts.push(<Btn key="yes" variant="approve" size="sm" icon={FiCheck} loading={busy === 'approve'} onClick={() => decide('approve')} aria-label={`Sign off ${who.name}'s request`}>Sign off</Btn>);
    }
    if (forManager && item.canSignOff) {
      parts.push(
        <button key="ovr" type="button" className="hrp-link hrp-rq__ovr" disabled={!!busy} aria-label={`Approve for manager: ${who.name}'s request`} onClick={() => decide('onBehalf')}>
          {busy === 'onBehalf' ? <span className="hrp-spin hrp-rq__spin" aria-hidden="true" /> : <FiCheck aria-hidden="true" />}
          Approve for manager
        </button>
      );
      if (!declining) {
        parts.push(
          <button key="dfm" type="button" className="hrp-link hrp-rq__ovr hrp-rq__cx" disabled={!!busy} aria-label={`Decline for manager: ${who.name}'s request`} onClick={startDecline}>
            <FiX aria-hidden="true" />
            Decline for manager
          </button>
        );
      }
    }
    if (item.needsBackup) {
      parts.push(
        <Link key="backup" to="../settings" className="hrp-rq__backup">
          <FiSettings aria-hidden="true" />
          Set a backup approver in Settings
        </Link>
      );
    } else if (!item.canSignOff) {
      parts.push(
        <span key="wait" className="hrp-rq__hold">
          {item.status === 'pending_hr' ? 'Waiting on the backup approver' : 'Their own request — not yours to approve'}
        </span>
      );
    }
    if (cancelLink) parts.push(cancelLink);
    actions = parts.length ? <div className="hrp-rq__acts">{parts}</div> : null;
  } else if (cancelLink) {
    actions = <div className="hrp-rq__acts">{cancelLink}</div>;
  }

  const showDetail = ((declining || cancelling) && !saved) || !!error;
  return (
    <tbody className="hrp-rq__grp" aria-labelledby={nameId}>
      <tr>
        <td data-label="Person">
          <div className="hrp-rq__nm">
            <Avatar id={who.id} name={who.name} size="md" />
            <div className="hrp-rq__nmtxt">
              <b id={nameId}>{who.name}</b>
              {wait && <span data-tone={wait.tone}>{wait.text}</span>}
              {!open && item.managerName && <span>{`Approver: ${item.managerName}`}</span>}
            </div>
          </div>
        </td>
        <td data-label="Request">
          <div className="hrp-rq__rq">
            {item.typeName && <Pill {...toneFor(item.typeName)} dot>{item.typeName}</Pill>}
            <div className="hrp-rq__rqtxt">
              <b>{shortRangeLabel(item.startDate, item.endDate)}</b>
              <span>{lengthOf(item)}</span>
            </div>
          </div>
        </td>
        <td data-label="Progress"><StageChips item={item} /></td>
        <td className="hrp-rq__td-act">{actions}</td>
      </tr>
      {showDetail && (
        <tr className="hrp-rq__detail">
          <td colSpan={4}>
            {declining && !saved && (
              <div className="hrp-ap__decline hrp-rq__decline">
                <Field label="Reason" labelNote="(optional · shown to them)">
                  <textarea ref={reasonRef} className="hrp-textarea" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Quarter close — could it move a week?" />
                </Field>
                <div className="hrp-ap__dbtns">
                  <Btn variant="ghost" size="sm" onClick={() => setDeclining(false)} disabled={!!busy}>Keep request</Btn>
                  <Btn variant="danger" size="sm" icon={FiX} loading={busy === 'decline'} onClick={() => decide('decline')}>Decline request</Btn>
                </div>
              </div>
            )}
            {cancelling && !saved && (
              <div className="hrp-ap__decline hrp-rq__decline hrp-rq__cxconfirm" role="group" aria-label={`Cancel ${who.name}'s request`}>
                <p className="hrp-rq__cxtext">
                  {`Cancel ${who.name}'s ${item.typeName || 'time off'} ${shortRangeLabel(item.startDate, item.endDate)}? Their hours come back, and they, their approver and HR are emailed.`}
                </p>
                <div className="hrp-ap__dbtns">
                  <Btn ref={keepRef} variant="ghost" size="sm" onClick={() => setCancelling(false)} disabled={!!busy}>Keep request</Btn>
                  <Btn variant="danger" size="sm" icon={FiX} loading={busy === 'cancel'} onClick={cancel}>Cancel request</Btn>
                </div>
              </div>
            )}
            {error && <p className="hrp-rq__err" role="alert"><FiAlertCircle aria-hidden="true" />{error}</p>}
          </td>
        </tr>
      )}
    </tbody>
  );
}

function SetupCard({ settings, people }) {
  const backup = settings && settings.backup;
  const person = backup ? ((people.data && people.data.people) || []).find((p) => p.id === backup.employeeId) : null;
  let when = 'Covers HR at all times';
  if (backup && (backup.from || backup.to)) {
    when = `Covers HR ${backup.from ? shortDate(backup.from) : 'now'} – ${backup.to ? shortDate(backup.to) : 'until removed'}`;
  }
  return (
    <Card feat delay={1} as="aside" className="hrp-rq-setup" aria-labelledby="hrp-rq-setup-title">
      <h2 className="hrp-card__title" id="hrp-rq-setup-title">How approvals work</h2>
      <ol className="hrp-rq-setup__steps">
        <li>
          <span className="hrp-req__n" aria-hidden="true">1</span>
          <div><b>Approver</b><span>Set on each person's profile, under Approvals.</span></div>
        </li>
        <li>
          <span className="hrp-req__n" aria-hidden="true">2</span>
          <div><b>HR</b><span>Final sign-off, always.</span></div>
        </li>
      </ol>
      <div className="hrp-rq-setup__lab">Backup approver</div>
      {backup && person ? (
        <div className="hrp-rq-setup__pick">
          <Avatar id={person.id} name={fullName(person)} size="sm" />
          <div><b>{fullName(person)}</b><span>{when}</span></div>
        </div>
      ) : (
        <div className="hrp-rq-setup__pick" data-tone="warn">
          <FiAlertCircle aria-hidden="true" />
          <div>
            <b>None set</b>
            <span>HR's own requests can't be signed off. <Link to="../settings" className="hrp-link">Choose a backup in Settings</Link></span>
          </div>
        </div>
      )}
      <p className="hrp-rq-setup__hint">
        No approver set → requests go straight to HR. If the approver is also HR, one approval covers both steps.
        Sick days skip approval; the approver and HR get a heads-up. Approving for a manager is noted in the change log.
      </p>
    </Card>
  );
}

const EMPTY = {
  hr: { title: 'Nothing is waiting on HR', body: 'Requests land here once the approver says yes — or straight away for people with no approver.' },
  manager: { title: 'Nothing is waiting on a manager', body: 'Requests still with an approver show here, so you can step in when someone is away.' },
  decided: { title: 'No decisions in the last 90 days', body: 'Approved, declined and cancelled requests show here for 90 days.' }
};

export function RequestsPage({ people, settings, types, onCounts, onToast }) {
  const [view, setView] = useState('hr');
  const [counts, setCounts] = useState(null);
  const reportCounts = useCallback((c) => {
    setCounts(c);
    if (onCounts) onCounts(c);
  }, [onCounts]);
  const queue = useQueue(view, reportCounts);
  const rows = queue.view === view && queue.data ? queue.data.requests || [] : null;

  // Queue rows carry the type's name only; the type list gives its color.
  const toneFor = useMemo(() => {
    const byName = new Map((types || []).map((t) => [t.name, t]));
    return (name) => typeTone(byName.get(name) || { name });
  }, [types]);

  const decided = ({ item, kind, status }) => {
    const name = item.requester ? item.requester.name : 'their';
    let text = 'Request declined';
    if (kind === 'cancel') text = item.requester ? `Cancelled ${name}'s request` : 'Request cancelled';
    if (kind === 'approve') text = `Signed off — ${name}'s ${item.typeName || 'time off'} is booked`;
    if (kind === 'onBehalf') text = status === 'approved' ? 'Approved for the manager and signed off' : 'Approved for the manager — now waiting on HR sign-off';
    onToast(text);
    queue.reload();
  };

  let table;
  if (!rows && queue.status === 'error') {
    table = (
      <EmptyState compact icon={FiAlertCircle} title="Couldn't load requests" action={<Btn variant="ghost" size="sm" onClick={queue.reload}>Try again</Btn>}>
        Something went wrong on our side.
      </EmptyState>
    );
  } else if (!rows) {
    table = <Skeleton label="Loading requests" lines={5} height={18} />;
  } else if (rows.length === 0) {
    const e = EMPTY[view];
    table = <EmptyState compact icon={view === 'decided' ? FiInbox : FiCheckCircle} title={e.title}>{e.body}</EmptyState>;
  } else {
    table = (
      <div className="hrp-table-wrap">
        <table className="hrp-table hrp-stack-table hrp-rq-table">
          <caption className="hrp-sr">{CAPTIONS[view]}</caption>
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Request</th>
              <th scope="col">{view === 'decided' ? 'Outcome' : 'Progress'}</th>
              <th scope="col"><span className="hrp-sr">Actions</span></th>
            </tr>
          </thead>
          {rows.map((item) => <RequestRows key={`${item.id}-${item.status}`} item={item} toneFor={toneFor} onDecided={decided} />)}
        </table>
      </div>
    );
  }

  return (
    <div className="hrp-page hrp-rq">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Requests</h1>
          <p className="hrp-sub">Every request and where it's waiting. Approving for a manager covers someone who's away.</p>
        </div>
      </div>
      <div className="hrp-chips hrp-rq__chips" role="group" aria-label="Show">
        {VIEWS.map((v) => (
          <button key={v.key} type="button" className="hrp-chip" aria-pressed={view === v.key} onClick={() => setView(v.key)}>
            {v.label}
            {v.count && counts && counts[v.count] > 0 && <>{' '}<span className="hrp-chip__n">{counts[v.count]}</span></>}
          </button>
        ))}
      </div>
      <div className="hrp-rq__cols">
        <Card delay={0} className="hrp-rq__card">
          {queue.status === 'error' && rows && (
            <div className="hrp-form-err hrp-rq__stale" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>This list didn't refresh.</span>
              <Btn variant="ghost" size="sm" onClick={queue.reload} iconAfter={FiArrowRight}>Try again</Btn>
            </div>
          )}
          {table}
        </Card>
        <SetupCard settings={settings} people={people} />
      </div>
    </div>
  );
}

export default RequestsPage;
